import { maybeStripRoomNumber } from "src/pages/configurator/Configurator.utils";
import {
  buildSummaryListEffectiveLineItems,
  filterSummaryEffectiveOptionGroups,
  getSummaryGroupsEffectivePriceInCents,
} from "src/utils/summary.utils";
import { Css } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { currencyFormatter } from "~utils";
import { SummaryPrintConfiguration_CheckoutConfigFragment } from "../SummaryPrint.page.generated";
import { ExteriorPDFCard } from "./Exterior.report";
import { FloorPlanPDFCard } from "./FloorPlan.report";
import { PDFPage } from "./PDFPage";
import { PrintHeader } from "./PrintHeader";
import { PrintListItem } from "./PrintListItem";

type SummaryReportProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  shouldDisplayPricing: boolean;
};

export function SummaryReport({ data, shouldDisplayPricing }: SummaryReportProps) {
  const { name, status, createdAt, reservedAt, expiresAt, customerEmail, optionGroups, totalPriceInCents } = data ?? {};

  return (
    <PDFPage>
      <div css={Css.relative.h100.w100.bgWhite.$}>
        <div css={Css.df.ma.fdc.gap2.p1.pb4.asc.maxContainer.$}>
          <PrintHeader
            status={status}
            expiredAt={expiresAt}
            createdAt={createdAt}
            reservedAt={reservedAt}
            configurationName={name}
            customerEmail={customerEmail}
          />
          {/* Customer and Build section */}
          <FloorPlanPDFCard data={data} shouldDisplayPricing={shouldDisplayPricing} />
          <ExteriorPDFCard optionGroups={optionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <StructuralOptionsPDFCard optionGroups={optionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <InteriorPDFCard optionGroups={optionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <InteriorPersonalizationPDFCard optionGroups={optionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <AddOnsPDFCard optionGroups={optionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <div css={Css.add({ breakInside: "avoid-page" }).$}>
            {/* Plan Cost and Estimated Total Cost */}
            {shouldDisplayPricing && (
              <div css={Css.df.fdc.aife.ptPx(12).add({ breakInside: "avoid-page" }).$}>
                <div css={Css.df.colGap1.$}>
                  <h6 css={Css.t18.fsPx(20).fw500.lhPx(32).gap1.$}>
                    <sup>*</sup>Est. Total Cost
                  </h6>
                  <h6 css={Css.t18.fsPx(20).fw500.lhPx(32).gap1.$}>{currencyFormatter(totalPriceInCents)}</h6>
                </div>
              </div>
            )}
            {/* Disclaimer */}
            <div id="disclaimer" css={Css.t12.pt2.tac.gray700.$}>
              <sup>*</sup>Estimated total cost includes site-specific conditions that may vary based on lot conditions.
              All options and prices will be confirmed by a Homebound representative prior to final signature.
            </div>
          </div>
        </div>
      </div>
    </PDFPage>
  );
}

type StructuralOptionsPDFCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function StructuralOptionsPDFCard({ optionGroups, shouldDisplayPricing }: StructuralOptionsPDFCardProps) {
  const floorPlanOptions = filterSummaryEffectiveOptionGroups(
    optionGroups,
    CheckoutConfigOptionGroupStep.OptionsStructural,
  );
  return (
    <PrintListItem
      title="Floor Plan Options"
      shouldDisplayPricing={shouldDisplayPricing}
      priceInCents={getSummaryGroupsEffectivePriceInCents(floorPlanOptions)}
      items={buildSummaryListEffectiveLineItems(floorPlanOptions, true)}
    />
  );
}

type InteriorPDFCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function InteriorPDFCard({ optionGroups, shouldDisplayPricing }: InteriorPDFCardProps) {
  const interiorDesignPackages = optionGroups.filter(
    (ccog) => ccog.isEnabled && ccog.step === CheckoutConfigOptionGroupStep.InteriorDesignPackage,
  );
  return (
    <div css={Css.df.fdc.$}>
      {interiorDesignPackages.map((ccog) => {
        return (
          <PrintListItem
            key={ccog.id}
            title={ccog.group.name}
            shouldDisplayPricing={shouldDisplayPricing}
            priceInCents={ccog.selectedOption!.totalPriceInCents}
            // Note: we don't show location here because it is implied by being in interiorDesignPackages section.
            items={[{ title: ccog.selectedOption!.option.name, optionCode: ccog.selectedOption!.option.code }]}
          />
        );
      })}
    </div>
  );
}

type InteriorPersonalizationPDFCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function InteriorPersonalizationPDFCard({ optionGroups, shouldDisplayPricing }: InteriorPersonalizationPDFCardProps) {
  const personalizationOptions = optionGroups.filter(
    (ccog) =>
      ccog.step === CheckoutConfigOptionGroupStep.InteriorPersonalization && ccog.isEnabled && !!ccog.selectedOption,
  );

  if (personalizationOptions.isEmpty) return null;

  return (
    <PrintListItem
      title="Options"
      shouldDisplayPricing={shouldDisplayPricing}
      priceInCents={personalizationOptions.sum((og) => og.selectedOption!.totalPriceInCents)}
      items={personalizationOptions.map((optionGroup) => ({
        title: `${optionGroup.selectedOption!.option.name}${optionGroup.group.location ? ` - ${maybeStripRoomNumber(optionGroup.group.location.name)}` : ""}`,
        priceInCents: optionGroup.selectedOption!.totalPriceInCents,
        optionCode: optionGroup.selectedOption!.option.code,
      }))}
    />
  );
}

type AddOnsCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function AddOnsPDFCard({ optionGroups, shouldDisplayPricing }: AddOnsCardProps) {
  const selectedAddOns = filterSummaryEffectiveOptionGroups(optionGroups, CheckoutConfigOptionGroupStep.OptionsAddon);

  return (
    <div css={Css.df.fdc.add({ breakInside: "avoid-page" }).$}>
      <PrintListItem
        title="Add-Ons"
        shouldDisplayPricing={shouldDisplayPricing}
        priceInCents={getSummaryGroupsEffectivePriceInCents(selectedAddOns)}
        items={buildSummaryListEffectiveLineItems(selectedAddOns, true)}
      />
    </div>
  );
}
